import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../utils/authService';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(true);
  
  useEffect(() => {
    // Get user data from authService if not passed as prop
    const userData = user || authService.getUser();
    const userRole = userData?.role;
    
    console.log('🔍 Dashboard component - checking user role:', userRole);
    console.log('📊 User data:', userData);
    
    if (!userData || !userRole) {
      console.log('❌ No user data or role found, redirecting to login');
      authService.logout();
      navigate('/login', { replace: true });
      return;
    }
    
    // Normalize role format (handle both "AGENT" and "ROLE_AGENT" formats)
    const normalizedRole = userRole?.startsWith('ROLE_') ? userRole : `ROLE_${userRole}`;
    
    console.log('🔄 Normalized role:', normalizedRole);
    
    // Add a small delay to ensure proper navigation
    setTimeout(() => {
      // Redirect to role-specific dashboard
      if (normalizedRole === 'ROLE_AGENT') {
        console.log('🎯 Redirecting agent to /agent/dashboard');
        navigate('/agent/dashboard', { replace: true });
      } else if (normalizedRole === 'ROLE_SUPPORT') {
        console.log('🎯 Redirecting support to /support/dashboard');
        navigate('/support/dashboard', { replace: true });
      } else if (normalizedRole === 'ROLE_ADMIN') {
        console.log('🎯 Redirecting admin to /admin/dashboard');
        navigate('/admin/dashboard', { replace: true });
      } else {
        console.log('⚠️ Unknown role, staying on generic dashboard');
        setRedirecting(false);
      }
    }, 100);
  }, [user, navigate]);

  // Show loading while redirecting
  if (redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  // Extract user data and role from the login response
  const userData = user?.user || user || authService.getUser(); // Handle both formats
  const userRole = userData?.role || 'ROLE_USER';
  
  const getDashboardTitle = () => {
    switch (userRole) {
      case 'ROLE_ADMIN':
        return 'Admin Dashboard';
      case 'ROLE_AGENT':
        return 'Agent Dashboard';
      case 'ROLE_SUPPORT':
        return 'Support Dashboard';
      default:
        return 'Dashboard';
    }
  };

  const getDashboardContent = () => {
    switch (userRole) {
      case 'ROLE_ADMIN':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <i className="fas fa-users text-blue-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Total Users</h3>
                  <p className="text-2xl font-bold text-blue-600">1,234</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <i className="fas fa-hotel text-green-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Active Hotels</h3>
                  <p className="text-2xl font-bold text-green-600">89</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <i className="fas fa-calendar-check text-yellow-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Bookings Today</h3>
                  <p className="text-2xl font-bold text-yellow-600">156</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <i className="fas fa-dollar-sign text-purple-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Revenue</h3>
                  <p className="text-2xl font-bold text-purple-600">$45,678</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'ROLE_AGENT':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <i className="fas fa-calendar-check text-blue-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">My Bookings</h3>
                  <p className="text-2xl font-bold text-blue-600">23</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <i className="fas fa-check-circle text-green-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirmed</h3>
                  <p className="text-2xl font-bold text-green-600">18</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <i className="fas fa-clock text-yellow-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Pending</h3>
                  <p className="text-2xl font-bold text-yellow-600">5</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'ROLE_SUPPORT':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Open Tickets</h3>
                  <p className="text-2xl font-bold text-red-600">12</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <i className="fas fa-user-headset text-blue-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">In Progress</h3>
                  <p className="text-2xl font-bold text-blue-600">8</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <i className="fas fa-check-double text-green-600 text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Resolved Today</h3>
                  <p className="text-2xl font-bold text-green-600">15</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Welcome to your Dashboard</h3>
            <p className="text-gray-600">
              Your personalized dashboard will be available once your account is approved and role is assigned.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Hotel Management System</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {userRole.replace('ROLE_', '')}
              </span>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {userData?.firstName?.charAt(0)}{userData?.lastName?.charAt(0)}
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {userData?.firstName} {userData?.lastName}
                </span>
              </div>
              <button
                onClick={onLogout}
                className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Dashboard Header */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{getDashboardTitle()}</h1>
                <p className="text-gray-600">Welcome back, {userData?.firstName} {userData?.lastName}</p>
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          <div>
            {getDashboardContent()}
          </div>

          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">Successfully logged in</p>
                  <p className="text-xs text-gray-500">Just now</p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">Dashboard loaded</p>
                  <p className="text-xs text-gray-500">1 minute ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;